import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@truefit/http-utils';

import { ConfigDetail, configRegistryKeys } from './useConfigsQuery';

export interface RestoreConfigVersionVariables {
  key: string;
  /** The historical version to copy forward. */
  version: number;
  /** Guards the restore exactly like a save, so it cannot clobber a fresh edit. */
  expectedVersion: number;
  note: string | null;
}

/**
 * Makes an earlier version current again.
 *
 * This appends — the restored content becomes a new, higher version that records
 * where it came from. Nothing between the restored version and now is deleted or
 * rewritten, so a restore is itself undoable by restoring what preceded it.
 */
export const useRestoreConfigVersionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, ...body }: RestoreConfigVersionVariables): Promise<ConfigDetail> => {
      const { data } = await post<ConfigDetail>(
        `/v1.0/internals/configs/${encodeURIComponent(key)}/restore`,
        body,
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: configRegistryKeys.all }),
  });
};

export default useRestoreConfigVersionMutation;
